import { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useFunding, type FundingCommitment, type CommitmentWithDetails, type FundingSourceType, type FundingCategory, type FundingStatus } from '../../hooks/useFunding';

interface Props {
  commitment?: CommitmentWithDetails;
  onClose: () => void;
}

export function FundingCommitmentModal({ commitment, onClose }: Props) {
  const { sources: initialSources, createSource, createCommitment, updateCommitment } = useFunding();
  const [sources, setSources] = useState(initialSources);
  const [formData, setFormData] = useState({
    source_id: commitment?.source_id || '',
    title: commitment?.title || '',
    category: commitment?.category || 'operating' as FundingCategory,
    commitment_amount: commitment?.commitment_amount?.toString() || '',
    convention_start_date: commitment?.convention_start_date || '',
    convention_end_date: commitment?.convention_end_date || '',
    allocation: commitment?.allocation || '',
    status: commitment?.status || 'planned' as FundingStatus,
    phase_tag: commitment?.phase_tag || '',
    notes: commitment?.notes || ''
  });
  const [showNewSource, setShowNewSource] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceType, setNewSourceType] = useState<FundingSourceType>('public');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSources(initialSources);
  }, [initialSources]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.source_id) {
      alert('Veuillez selectionner ou creer un financeur');
      return;
    }

    setSaving(true);

    try {
      if (commitment) {
        await updateCommitment(commitment.id, {
          ...formData,
          commitment_amount: parseFloat(formData.commitment_amount)
        });
      } else {
        await createCommitment({
          ...formData,
          commitment_amount: parseFloat(formData.commitment_amount)
        } as any);
      }
      onClose();
    } catch (error) {
      console.error('Error saving commitment:', error);
      alert('Erreur lors de l\'enregistrement. Veuillez reessayer.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSource = async () => {
    if (!newSourceName.trim()) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('funding_sources')
        .insert([{
          name: newSourceName,
          source_type: newSourceType,
          org_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      setSources([...sources, data]);
      setFormData({ ...formData, source_id: data.id });
      setNewSourceName('');
      setShowNewSource(false);
    } catch (error) {
      console.error('Error creating source:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {commitment ? 'Modifier l\'engagement' : 'Nouvel engagement'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Financeur *
            </label>
            {!showNewSource ? (
              <div className="flex gap-2">
                <select
                  value={formData.source_id}
                  onChange={(e) => setFormData({ ...formData, source_id: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                >
                  <option value="">Selectionner un financeur</option>
                  {sources.map(source => (
                    <option key={source.id} value={source.id}>
                      {source.name} ({source.source_type})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowNewSource(true)}
                  className="px-3 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSourceName}
                  onChange={(e) => setNewSourceName(e.target.value)}
                  placeholder="Nom du financeur"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <select
                  value={newSourceType}
                  onChange={(e) => setNewSourceType(e.target.value as FundingSourceType)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="public">Public</option>
                  <option value="private">Prive</option>
                  <option value="bank">Banque</option>
                  <option value="internal">Interne</option>
                </select>
                <button
                  type="button"
                  onClick={handleCreateSource}
                  className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                >
                  Creer
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewSource(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ex: Subvention fonctionnement 2024-2025"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categorie *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as FundingCategory })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              >
                <option value="operating">Fonctionnement</option>
                <option value="investment">Investissement</option>
                <option value="equity">Fonds propres</option>
                <option value="loan">Pret</option>
              </select>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant Acquis/Promis *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.commitment_amount}
                  onChange={(e) => setFormData({ ...formData, commitment_amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                  min="0"
                  step="0.01"
                />
                <span className="absolute right-3 top-2 text-gray-500">€</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Convention start */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Debut periode conventionnee
              </label>
              <input
                type="date"
                value={formData.convention_start_date}
                onChange={(e) => setFormData({ ...formData, convention_start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Convention end */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fin periode conventionnee
              </label>
              <input
                type="date"
                value={formData.convention_end_date}
                onChange={(e) => setFormData({ ...formData, convention_end_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etat *
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as FundingStatus })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              >
                <option value="planned">Prevu</option>
                <option value="promised">Promis</option>
                <option value="signed">Signe</option>
                <option value="in_progress">En cours</option>
                <option value="paid">Verse</option>
                <option value="cancelled">Annule</option>
              </select>
            </div>

            {/* Phase tag */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phase (optionnel)
              </label>
              <input
                type="text"
                value={formData.phase_tag}
                onChange={(e) => setFormData({ ...formData, phase_tag: e.target.value })}
                placeholder="Ex: Amorcage, Modele economique"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Allocation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Affectation
            </label>
            <input
              type="text"
              value={formData.allocation}
              onChange={(e) => setFormData({ ...formData, allocation: e.target.value })}
              placeholder="Ex: Salaires formateurs, Achat machine"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : (commitment ? 'Modifier' : 'Creer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
